import { io, Socket } from 'socket.io-client'
import { store } from '@/store/store'
import { MessageSource } from '@/types/chat.types'

// Types for socket events matching backend
interface ServerToClientEvents {
  'chat:joined': (data: { sessionId: string }) => void
  'chat:session-created': (data: { sessionId: string; originalTempSessionId: string }) => void
  'chat:session-updated': (data: { sessionId: string; title: string; updatedAt: string }) => void
  'chat:message:start': (data: MessageStartData) => void
  'chat:message:chunk': (data: MessageChunkData) => void
  'chat:message:citation': (data: CitationData) => void
  'chat:message:chart': (data: ChartEventData) => void
  'chat:message:hierarchical_table': (data: HierarchicalTableEventData) => void
  'chat:message:complete': (data: MessageCompleteData) => void
  'chat:message:end': (data: { messageId: string; timestamp: Date }) => void
  'chat:message:error': (data: MessageErrorData) => void
  'chat:status': (data: StatusData) => void
  'chat:typing': (data: { userId: string; isTyping: boolean }) => void
  'store-memory:updated': (data: StoreMemoryUpdateData) => void
  'error': (data: { message: string }) => void
  'pong': () => void
}

export interface StoreMemoryUpdateData {
  storeId: string
  action: 'created' | 'updated' | 'deleted'
  noteId?: string
  timestamp: string
}

interface ClientToServerEvents {
  'chat:join': (sessionId: string) => void
  'chat:leave': (sessionId: string) => void
  'chat:message': (data: SendMessageData) => void
  'chat:typing': (data: TypingData) => void
  'chat:cancel': (messageId: string) => void
  'ping': () => void
}

// Data types
export interface SendMessageData {
  sessionId: string
  content: string
  role: 'USER' | 'ASSISTANT'
  agentType?: 'HR' | 'OPS'
  messageId?: string
}

export interface TypingData {
  sessionId: string
  isTyping: boolean
}

export interface MessageStartData {
  messageId: string
  sessionId: string
  timestamp: Date
}

export interface MessageChunkData {
  messageId: string
  sessionId: string
  chunk: string
  index: number
}

export interface CitationData {
  messageId: string
  citation: {
    documentId: string
    documentTitle: string
    pageNumber: number
    relevanceScore: number
    snippet: string
  }
}

export interface MessageCompleteData {
  messageId: string
  fullContent: string
  citations: CitationData['citation'][]
  messageSources?: MessageSource[]
  timestamp: Date
}

export interface MessageErrorData {
  messageId: string
  error: string
  code?: string
}

export interface StatusData {
  messageId: string
  status: 'searching' | 'generating' | 'processing' | 'complete' | 'error'
  message?: string
}

export interface ChartEventData {
  messageId: string
  chart: {
    type: 'chart'
    chartType: 'bar' | 'line' | 'pie' | 'doughnut' | 'table'
    title: string
    data: {
      labels: string[]
      datasets: Array<{
        label: string
        data: number[]
        backgroundColor?: string | string[]
        borderColor?: string | string[]
        borderWidth?: number
        fill?: boolean
      }>
    }
    options?: any
    metadata?: any
  }
}

export interface HierarchicalTableEventData {
  messageId: string
  table: {
    type: 'hierarchical_table'
    title: string
    data: Record<string, any>[]
    hierarchy?: string[]
    metadata?: {
      queryId?: string
      source?: string
      generatedAt?: string
    }
  }
}

export type SocketEventCallback<T = any> = (data: T) => void

class SocketService {
  private socket: any = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private isConnecting = false
  private eventListeners: Map<string, Set<SocketEventCallback>> = new Map()
  private socketEventListeners: Map<string, Set<Function>> = new Map()

  constructor() {
    this.setupVisibilityHandlers()
  }

  connect(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve()
        return
      }

      if (this.isConnecting) {
        // Wait for existing connection attempt to complete
        const checkConnection = () => {
          if (this.socket?.connected) {
            resolve()
          } else if (!this.isConnecting) {
            reject(new Error('Connection failed'))
          } else {
            setTimeout(checkConnection, 100)
          }
        }
        checkConnection()
        return
      }

      this.isConnecting = true
      
      const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:5000'
      const socketUrl = apiBaseUrl.replace('/api', '')

      try {
        this.socket = io(socketUrl, {
          auth: { token },
          transports: ['websocket', 'polling'],
          timeout: 20000,
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay,
          autoConnect: false
        })

        // Ensure socket is properly initialized before setting up event listeners
        if (!this.socket) {
          this.isConnecting = false
          reject(new Error('Failed to initialize socket'))
          return
        }

        // Apply any buffered event listeners registered before connect()
        this.socketEventListeners.forEach((callbacks, event) => {
          callbacks.forEach(cb => {
            this.socket.on(event, cb)
          })
        })

        // Connection success
        this.socket.on('connect', () => {
          console.log('✅ WebSocket connected')
          this.isConnecting = false
          this.reconnectAttempts = 0
          this.notifyListeners('connection:success', { connected: true })
          resolve()
        })

        // Connection error
        this.socket.on('connect_error', (error: Error) => {
          console.error('❌ WebSocket connection error:', error)
          this.isConnecting = false
          this.notifyListeners('connection:error', { error: error.message })
          reject(error)
        })

        // Disconnection
        this.socket.on('disconnect', (reason: string) => {
          console.log('🔌 WebSocket disconnected:', reason)
          this.notifyListeners('connection:lost', { reason })
          
          if (reason === 'io server disconnect') {
            // Server initiated disconnect, don't reconnect automatically
            this.notifyListeners('connection:terminated', { reason })
          }
        })

        // Reconnect attempt
        this.socket.on('reconnect_attempt', (attemptNumber: number) => {
          console.log(`🔄 Reconnect attempt ${attemptNumber}/${this.maxReconnectAttempts}`)
          this.notifyListeners('connection:reconnecting', { attempt: attemptNumber })
        })

        // Reconnect success
        this.socket.on('reconnect', (attemptNumber: number) => {
          console.log('✅ WebSocket reconnected after', attemptNumber, 'attempts')
          this.reconnectAttempts = 0
          this.notifyListeners('connection:restored', { attempts: attemptNumber })
        })

        // Reconnect failed
        this.socket.on('reconnect_failed', () => {
          console.error('❌ WebSocket reconnection failed')
          this.notifyListeners('connection:failed', { maxAttemptsReached: true })
        })

        // Start connection
        this.socket.connect()
      } catch (error) {
        console.error('Failed to create socket:', error)
        this.isConnecting = false
        reject(error)
      }
    })
  }

  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnecting WebSocket')
      this.socket.disconnect()
      this.socket = null
    }
    this.isConnecting = false
    this.reconnectAttempts = 0
    this.eventListeners.clear()
    this.socketEventListeners.clear()
  }

  // Check connection status
  isConnected(): boolean {
    return this.socket?.connected || false
  }

  // Emit events to server
  emit<K extends keyof ClientToServerEvents>(
    event: K,
    ...args: Parameters<ClientToServerEvents[K]>
  ): void {
    if (!this.socket?.connected) {
      console.warn('Socket not connected, cannot emit:', event)
      return
    }
    this.socket.emit(event, ...args)
  }

  // Listen for events from server
  // Listeners are buffered so they can be registered before the socket connects
  on<K extends keyof ServerToClientEvents>(
    event: K,
    callback: ServerToClientEvents[K]
  ): void {
    // Always store in the buffer
    if (!this.socketEventListeners.has(event)) {
      this.socketEventListeners.set(event, new Set())
    }
    this.socketEventListeners.get(event)!.add(callback as Function)

    // Register on socket immediately if available
    if (this.socket && typeof this.socket.on === 'function') {
      this.socket.on(event, callback)
    }
  }

  // Remove event listener
  off<K extends keyof ServerToClientEvents>(
    event: K,
    callback?: ServerToClientEvents[K]
  ): void {
    // Remove from buffer
    if (callback) {
      const listeners = this.socketEventListeners.get(event)
      if (listeners) {
        listeners.delete(callback as Function)
        if (listeners.size === 0) this.socketEventListeners.delete(event)
      }
    } else {
      this.socketEventListeners.delete(event)
    }

    // Remove from socket if available
    if (this.socket && typeof this.socket.off === 'function') {
      if (callback) {
        this.socket.off(event, callback)
      } else {
        this.socket.off(event)
      }
    }
  }

  // Listen for connection events (internal use)
  onConnectionEvent(event: string, callback: SocketEventCallback): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event)!.add(callback)

    // Return cleanup function
    return () => {
      const listeners = this.eventListeners.get(event)
      if (listeners) {
        listeners.delete(callback)
        if (listeners.size === 0) {
          this.eventListeners.delete(event)
        }
      }
    }
  }

  private notifyListeners(event: string, data: any): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error('Error in socket event listener:', error)
        }
      })
    }
  }

  // Chat-specific convenience methods
  joinSession(sessionId: string): void {
    this.emit('chat:join', sessionId)
  }

  leaveSession(sessionId: string): void {
    this.emit('chat:leave', sessionId)
  }

  sendMessage(data: SendMessageData): void {
    this.emit('chat:message', data)
  }

  sendTyping(data: TypingData): void {
    this.emit('chat:typing', data)
  }

  cancelMessage(messageId: string): void {
    this.emit('chat:cancel', messageId)
  }

  // Ping for connection health check
  ping(): void {
    this.emit('ping')
  }

  // Handle browser visibility changes
  private setupVisibilityHandlers(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.socket && !this.socket.connected) {
          // Reconnect when tab becomes visible and socket is disconnected
          console.log('🔄 Tab visible, reconnecting...')
          this.socket.connect()
        }
      })
    }

    // Handle network status changes
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        console.log('🌐 Network online, checking connection...')
        if (this.socket && !this.socket.connected) {
          this.socket.connect()
        }
      })

      window.addEventListener('offline', () => {
        console.log('🌐 Network offline')
        this.notifyListeners('connection:offline', { offline: true })
      })
    }
  }

  // Get connection state for UI
  getConnectionState(): {
    connected: boolean
    connecting: boolean
    reconnectAttempts: number
    maxReconnectAttempts: number
  } {
    return {
      connected: this.isConnected(),
      connecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    }
  }
}

// Singleton instance
export const socketService = new SocketService()
export default socketService